// src/hooks/useNotification.ts
// Centralized notification hook using notistack

import { useSnackbar, VariantType } from "notistack";
import { useCallback } from "react";

interface NotificationOptions {
  variant?: VariantType;
  autoHideDuration?: number;
  persist?: boolean;
}

export const useNotification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showNotification = useCallback(
    (message: string, options?: NotificationOptions) => {
      return enqueueSnackbar(message, {
        variant: options?.variant || "default",
        autoHideDuration: options?.autoHideDuration || 4000,
        persist: options?.persist || false,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    },
    [enqueueSnackbar]
  );

  const success = useCallback(
    (message: string, autoHide = 3000) =>
      showNotification(message, { variant: "success", autoHideDuration: autoHide }),
    [showNotification]
  );

  const error = useCallback(
    (message: string, autoHide = 5000) =>
      showNotification(message, { variant: "error", autoHideDuration: autoHide }),
    [showNotification]
  );

  const warning = useCallback(
    (message: string, autoHide = 4000) =>
      showNotification(message, { variant: "warning", autoHideDuration: autoHide }),
    [showNotification]
  );

  const info = useCallback(
    (message: string, autoHide = 4000) =>
      showNotification(message, { variant: "info", autoHideDuration: autoHide }),
    [showNotification]
  );

  return {
    showNotification,
    success,
    error,
    warning,
    info,
    close: closeSnackbar,
  };
};

export default useNotification;
